/**
 * Neighbors scan tracking: every multi-device scan is recorded by the
 * backend (scan_id in the start response) and its devices' progress is
 * read from GET /tree-mgmt/neighbors/scans/:id. The backend sends the
 * command in batches (batch_size devices at a time); a device gets its
 * results when its neighbors report is updated after the command was
 * sent to it.
 *
 * The tracker dialog is mounted once (NeighborScanTrackerHost); the scan
 * API calls announce every started scan through a window event and the
 * host opens the tracker on it.
 */

export const NEIGHBOR_SCAN_STARTED_EVENT = 'nm:neighbor-scan-started';
export const NEIGHBOR_SCAN_OPEN_EVENT = 'nm:neighbor-scan-open';

// Views that show scans in place (the Neighbours tab's Recent Scans)
// register here; a started scan goes to them instead of the tracker
// dialog when one is mounted.
const scanTargets = new Set();

/**
 * Register a view that shows a started scan itself. `show(scanId)` returns
 * true when it took the scan. Returns the unregister function.
 */
export function registerNeighborScanTarget(show) {
  scanTargets.add(show);
  return () => scanTargets.delete(show);
}

/**
 * Announce a scan start response: a registered view shows it, otherwise
 * the tracker dialog opens on it (detail.handled tells the host).
 */
export function announceNeighborScan(result) {
  if (!result?.scan_id || typeof window === 'undefined') return;
  let handled = false;
  scanTargets.forEach((show) => {
    try {
      if (show(result.scan_id)) handled = true;
    } catch {
      // a broken target must not stop the announcement
    }
  });
  window.dispatchEvent(new CustomEvent(NEIGHBOR_SCAN_STARTED_EVENT, {
    detail: {
      scanId: result.scan_id,
      sent: Number(result.commands_sent) || 0,
      targeted: Number(result.devices_targeted) || 0,
      batchSize: Number(result.batch_size) || 0,
      handled,
    },
  }));
}

/** Open the tracker on one scan, or on the recent-scans list (null). */
export function openNeighborScanTracker(scanId = null) {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent(NEIGHBOR_SCAN_OPEN_EVENT, { detail: { scanId } }));
}

/** Per-device states, in the order they are shown. */
export const NEIGHBOR_SCAN_STATUS = Object.freeze({
  received: { label: 'Got results', color: 'success', help: 'A neighbors report arrived after the command was sent.' },
  scanning: { label: 'Scanning', color: 'warning', help: 'The device confirmed the command and is scanning; its report is expected by the deadline.' },
  sent: { label: 'Sent', color: 'info', help: 'The command was delivered; waiting for the device to confirm it.' },
  unconfirmed: { label: 'Unconfirmed', color: 'default', help: 'The device did not confirm the command in time. Its slot went to the next device; a report from it still counts until its deadline.' },
  queued: { label: 'Waiting for a slot', color: 'default', help: 'Not sent yet: it starts when a scanning device finishes, times out, or does not confirm.' },
  overdue: { label: 'No results', color: 'error', help: 'No report by the deadline. A report that still arrives is picked up.' },
  not_sent: { label: 'Not sent', color: 'default', help: 'The command was not sent to this device.' },
});

const DISPATCH_LABELS = {
  failed_to_send: 'Command not delivered',
  failed_to_store: 'Command not stored',
  skipped: 'Skipped',
  not_found: 'Unknown device',
  cancelled: 'Cancelled',
};

/** Why a device was not sent the scan, e.g. "Skipped: device not connected". */
export function describeDispatch(device) {
  if (!device || ['sent', 'queued', 'unconfirmed'].includes(device.dispatch)) return '';
  const label = DISPATCH_LABELS[device.dispatch] || device.dispatch || 'Not sent';
  return device.error ? `${label}: ${device.error}` : label;
}

/** Devices to scan again: no results, unconfirmed, undelivered, cancelled, or back online. */
export function rescanCandidates(view) {
  return (view?.devices || [])
    .filter((d) => d.status === 'overdue' || d.status === 'unconfirmed'
      || d.dispatch === 'failed_to_send' || d.dispatch === 'failed_to_store' || d.dispatch === 'cancelled'
      || (d.dispatch === 'skipped' && d.connected))
    .map((d) => d.serial);
}

/** Devices shown for a filter: all, active (queued/sent/scanning/unconfirmed), missing (overdue/unconfirmed), received, not_sent. */
export function filterScanDevices(view, filter) {
  const devices = view?.devices || [];
  switch (filter) {
    case 'active': return devices.filter((d) => ['queued', 'sent', 'scanning', 'unconfirmed'].includes(d.status));
    case 'missing': return devices.filter((d) => d.status === 'overdue' || d.status === 'unconfirmed');
    case 'received': return devices.filter((d) => d.status === 'received');
    case 'not_sent': return devices.filter((d) => d.status === 'not_sent');
    default: return devices;
  }
}

/** Share of devices that have finished, one way or another (0–100). */
export function scanProgressPct(summary) {
  if (!summary?.targeted) return 0;
  const finished = (summary.received || 0) + (summary.overdue || 0) + (summary.not_sent || 0);
  return Math.round((finished / summary.targeted) * 100);
}

/** Devices still to be heard from (queued, sent, scanning or unconfirmed). */
export function scanOpenCount(summary) {
  if (!summary) return 0;
  return (summary.queued || 0) + (summary.sent || 0) + (summary.scanning || 0) + (summary.unconfirmed || 0);
}

const fmtDuration = (sec) => {
  const s = Math.max(0, Math.round(sec));
  if (s < 60) return `${s} s`;
  const m = Math.round(s / 60);
  return m < 60 ? `${m} min` : `${Math.floor(m / 60)} h ${m % 60} min`;
};

/**
 * One-paragraph explanation of how this scan runs, for the tracker:
 * batching, per-device time, confirmation wait, and what happens on
 * timeouts.
 */
export function describeScanPlan(view) {
  if (!view) return '';
  const n = view.summary?.targeted || 0;
  const batch = Number(view.batch_size) || 0;
  const perDevice = fmtDuration(view.device_scan_sec || 0);
  const parts = [];
  parts.push(batch > 0
    ? `${n} device${n === 1 ? '' : 's'}, ${batch} scanning at a time; the rest wait their turn.`
    : `${n} device${n === 1 ? '' : 's'}, all scanning at the same time.`);
  parts.push(`Each device scans for about ${perDevice} (${view.chan_scan_interval} s per channel, ${view.all_freq ? 'all channels' : 'known channels'}).`);
  parts.push(`A device that does not confirm the command within ${view.ack_timeout_sec || 20} s gives its slot to the next one; a device that confirms but sends no report by its deadline is marked "No results" and the next one starts.`);
  if (view.status === 'active' && view.expected_done_at) {
    parts.push(`Expected to finish by ${new Date(view.expected_done_at).toLocaleTimeString()}.`);
  }
  return parts.join(' ');
}

// Status order for sorting: what needs attention first.
const STATUS_RANK = { overdue: 0, unconfirmed: 1, not_sent: 2, queued: 3, sent: 4, scanning: 5, received: 6 };

/**
 * Devices sorted by a column: 'device' (name, then serial), 'status'
 * (attention first), 'report' (report time; devices without one last),
 * 'sent', 'neighbors'. dir is 'asc' or 'desc'; ties keep the scan's order.
 */
export function sortScanDevices(devices, column, dir = 'asc') {
  const list = (devices || []).map((d, i) => [d, i]);
  const time = (iso) => (iso ? Date.parse(iso) : null);
  const key = (d) => {
    switch (column) {
      case 'status': return STATUS_RANK[d.status] ?? 99;
      case 'report': return time(d.report_at);
      case 'sent': return time(d.sent_at);
      case 'neighbors': return d.status === 'received' ? (d.neighbors || 0) : null;
      default: return `${d.name || ''}\u0000${d.serial}`.toLowerCase();
    }
  };
  const sign = dir === 'desc' ? -1 : 1;
  list.sort(([a, ia], [b, ib]) => {
    const ka = key(a);
    const kb = key(b);
    // Missing values always last, whatever the direction.
    if (ka == null && kb == null) return ia - ib;
    if (ka == null) return 1;
    if (kb == null) return -1;
    if (ka < kb) return -sign;
    if (ka > kb) return sign;
    return ia - ib;
  });
  return list.map(([d]) => d);
}

/** "169 of 237 site devices online when started", or '' when not recorded. */
export function describeSiteAtStart(view) {
  if (!view || !(Number(view.site_devices) > 0)) return '';
  return `${view.site_online || 0} of ${view.site_devices} site devices online when started`;
}

/** Short outcome line for a finished scan. */
export function describeScanOutcome(view) {
  const s = view?.summary;
  if (!s) return '';
  const bits = [`${s.received} got results`];
  if (s.overdue) bits.push(`${s.overdue} no results`);
  if (s.not_sent) bits.push(`${s.not_sent} not sent`);
  const head = view.status === 'cancelled' ? 'Cancelled' : 'Finished';
  return `${head}: ${bits.join(', ')}.`;
}
